import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { KeyRound } from "lucide-react";
import { type FormEvent, useState } from "react";
import { ErrorMessage } from "@/components/error-message";
import { Button } from "@/components/ui/button";
import { Dialog } from "@/components/ui/dialog";
import { Field, Input } from "@/components/ui/field";
import { useI18n } from "@/i18n";
import { ApiError, api } from "@/lib/api";
import { meQuery } from "./queries";

/** After signing in with a temporary password, the person must choose their own first. */
export function ForcePasswordChange() {
  const { t } = useI18n();
  const queryClient = useQueryClient();
  const me = useQuery(meQuery);
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const save = useMutation({
    mutationFn: () => api.post("/auth/password", { password }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["me"] }),
  });
  if (!me.data?.user.mustChangePassword) return null;
  const mismatch = confirm.length > 0 && confirm !== password;
  return (
    <Dialog
      open
      onClose={() => undefined}
      size="sm"
      title={t("account.mustChangeTitle")}
      description={t("account.mustChangeHint")}
      footer={
        <Button
          type="submit"
          form="force-password"
          loading={save.isPending}
          disabled={password.length < 10 || mismatch || confirm.length === 0}
        >
          <KeyRound aria-hidden />
          {t("account.setPassword")}
        </Button>
      }
    >
      <form
        id="force-password"
        className="space-y-3"
        onSubmit={(e: FormEvent) => {
          e.preventDefault();
          save.mutate();
        }}
      >
        <Field
          label={t("account.newPassword")}
          hint={t("account.passwordRules")}
          error={save.error instanceof ApiError ? save.error.fieldError("password") : undefined}
        >
          {(p) => (
            <Input
              {...p}
              autoFocus
              type="password"
              autoComplete="new-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
          )}
        </Field>
        <Field
          label={t("account.confirmPassword")}
          error={mismatch ? t("account.passwordMismatch") : undefined}
        >
          {(p) => (
            <Input
              {...p}
              type="password"
              autoComplete="new-password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
            />
          )}
        </Field>
        <ErrorMessage error={save.error} />
      </form>
    </Dialog>
  );
}
